import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const EASE = [0.16, 1, 0.3, 1];

export function Reveal({ children, delay = 0, y = 36, className = '', once = true }) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once, amount: 0.15 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
}

export function MaskLine({ children, delay = 0, className = '', as: Tag = 'span' }) {
  return (
    <span className={`block overflow-hidden ${className}`}>
      <motion.span
        className="block"
        initial={{ y: '115%' }}
        animate={{ y: '0%' }}
        transition={{ duration: 1.1, delay, ease: EASE }}
      >
        <Tag className="block">{children}</Tag>
      </motion.span>
    </span>
  );
}

export function ParallaxImage({ src, alt, className = '', imgClassName = '', speed = 0.18, opacity = 1 }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });
  const y = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  return (
    <div ref={ref} className={`relative overflow-hidden ${className}`}>
      <motion.img
        src={src}
        alt={alt}
        style={{ y, opacity }}
        className={`absolute inset-0 h-full w-full object-cover scale-[1.18] ${imgClassName}`}
      />
    </div>
  );
}

export function SectionHead({ overline, title, link, linkLabel }) {
  return (
    <Reveal className="mb-10 flex items-end justify-between gap-6 md:mb-14">
      <div>
        <p className="meta-label mb-4 text-brayo-blue">{overline}</p>
        <h2 className="display-title text-4xl text-brayo-charcoal sm:text-5xl lg:text-6xl">{title}</h2>
      </div>
      {link && (
        <a
          data-testid={`section-link-${linkLabel ? linkLabel.toLowerCase().replace(/\s+/g, '-') : 'view-all'}`}
          href={link}
          className="arrow-slide meta-label hidden items-center gap-2 text-brayo-charcoal transition-colors hover:text-brayo-blue md:flex"
        >
          {linkLabel || 'View All'}
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M5 12h14M13 6l6 6-6 6" />
          </svg>
        </a>
      )}
    </Reveal>
  );
}

export default Reveal;